/**
 * Every ingredient's flavor scores: the rule, then the override on top
 * (PLAN-FLAVOR D-FL4). This is the only door the bridge uses.
 *
 * Scores are 0–5 per axis. Core's `FLAVOR_CURVE` constants were calibrated
 * against this scale and `maltPotency`, and `flavor-calibration.test.ts` in
 * commands pins them, so a change to the rules here can fail that test.
 */

import type { AxisScores, FlavorWheel, MaltPhClass } from '@beerwright/core';
import { getFermentable, getHop, getYeast } from '../catalog';
import { hopRuleScores } from './hop-rules';
import { maltRuleScores } from './malt-rules';
import { FERMENTABLE_OVERRIDES, HOP_OVERRIDES, YEAST_OVERRIDES } from './overrides';
import { yeastRuleScores } from './yeast-rules';

export { maltPotency } from './malt-rules';
export { STYLE_BANDS, styleBands } from './style-bands';

function merge<W extends FlavorWheel>(
  rule: AxisScores<W>,
  override: AxisScores<W> | undefined,
): AxisScores<W> {
  return override === undefined ? rule : { ...rule, ...override };
}

export function hopScores(catalogId: string): AxisScores<'hop'> | null {
  const hop = getHop(catalogId);
  if (hop === undefined) return null;
  return merge<'hop'>(hopRuleScores(hop.flavorTags), HOP_OVERRIDES[catalogId]);
}

export function fermentableScores(row: {
  readonly catalogId: string | null;
  readonly maltClass: MaltPhClass;
  readonly colorLovibond: number;
}): AxisScores<'malt'> {
  const entry = row.catalogId === null ? undefined : getFermentable(row.catalogId);
  const rule = maltRuleScores({
    maltClass: row.maltClass,
    colorLovibond: row.colorLovibond,
    tags: entry?.tags ?? [],
  });
  return merge<'malt'>(
    rule,
    row.catalogId === null ? undefined : FERMENTABLE_OVERRIDES[row.catalogId],
  );
}

export function yeastScores(row: {
  readonly catalogId: string | null;
  readonly type: string;
}): AxisScores<'fermentation'> | null {
  const entry = row.catalogId === null ? undefined : getYeast(row.catalogId);
  const rule = yeastRuleScores({ type: entry?.type ?? row.type, tags: entry?.tags ?? [] });
  const merged = merge<'fermentation'>(
    rule,
    row.catalogId === null ? undefined : YEAST_OVERRIDES[row.catalogId],
  );
  return Object.keys(merged).length === 0 ? null : merged;
}
export {
  ALLOWED_HIGH,
  ASKED_FOR_LOW,
  hopStyleRank,
  hopsSuitingStyle,
  isTraditionalHop,
  LOUD_HOP_NOTE,
  STYLE_FIT_LIST_CAP,
  type StyleFitList,
  stylesSuitedByHop,
  stylesSuitedByYeast,
  traditionalHops,
  yeastStyleRank,
  yeastsSuitingStyle,
} from './style-fit';
export { STYLE_TRADITIONS, type StyleTradition, styleTradition } from './style-traditions';
